<template>
	<component :is="wrapperTag" class="page-two-columns" :class="cssClass" @submit.prevent="onSubmit">
		<template v-for="(col, colIdx) in ['left', 'right']" :key="col">
			<div
				v-if="isSticky(col)"
				class="page-two-columns-column page-two-columns__column"
				:class="`page-two-columns__column--${col}`"
			>
				<div class="page-two-columns-column__content">
					<div v-if="hasSlot(`${col}-before`)" class="page-two-columns-column__before">
						<slot :name="`${col}-before`" v-bind="{ updateSidebar }" />
					</div>

					<div data-v-sticky-container class="page-two-columns-column__sticky-container">
						<template v-if="isiOS">
							<div
								:ref="el => setListRef(el, colIdx)"
								class="page-two-columns-column__sticky page-two-columns-column__sticky--native"
							>
								<div class="page-two-columns-column__inner">
									<slot :name="col" v-bind="{ updateSidebar }" />
								</div>
							</div>
						</template>

						<template v-else>
							<div
								:ref="el => setListRef(el, colIdx)"
								v-sticky="stickyOptions"
								class="page-two-columns-column__sticky"
							>
								<div class="page-two-columns-column__inner" data-v-sticky-inner>
									<slot :name="col" v-bind="{ updateSidebar }" />
								</div>
							</div>
						</template>
					</div>

					<div v-if="hasSlot(`${col}-after`)" class="page-two-columns-column__after">
						<slot :name="`${col}-after`" v-bind="{ updateSidebar }" />
					</div>
				</div>
			</div>

			<div
				v-else
				class="page-two-columns-column page-two-columns__column"
				:class="`page-two-columns__column--${col}`"
			>
				<div class="page-two-columns-column__content">
					<div v-if="hasSlot(`${col}-before`)" class="page-two-columns-column__before">
						<slot :name="`${col}-before`" v-bind="{ updateSidebar }" />
					</div>

					<div class="page-two-columns-column__inner">
						<slot :name="col" v-bind="{ updateSidebar }" />
					</div>

					<div v-if="hasSlot(`${col}-after`)" class="page-two-columns-column__after">
						<slot :name="`${col}-after`" v-bind="{ updateSidebar }" />
					</div>
				</div>
			</div>
		</template>
	</component>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, ref } from 'vue';

import { useArrayRefs, useHasDynamicSlot } from '@/composables';
import { sticky } from '@/directives';
import provideValidator from '@/mixins/validation/provideValidator';
import { enumValidator, isiOS } from '@/tools/helpers';
import { AnyObject } from '@/tools/types';

export enum GridDivision {
	Equal = 'equal',
	FiveToSeven = 'five-to-seven',
	OneThird = 'one-third',
	OneFourth = 'one-fourth',
}
export enum StickyColumn {
	None = 'none',
	Left = 'left',
	Right = 'right',
}

const PADDING_TOP = 65;
const PADDING_BOTTOM = 120;

export default defineComponent({
	directives: {
		sticky,
	},

	mixins: [provideValidator()],

	props: {
		division: {
			type: String as PropType<`${GridDivision}`>,
			default: GridDivision.OneThird,
			validator: enumValidator<GridDivision>(GridDivision),
		},

		sticky: {
			type: String as PropType<`${StickyColumn}`>,
			default: StickyColumn.None,
			validator: enumValidator<StickyColumn>(StickyColumn),
		},

		withValidation: {
			type: Boolean,
			default: false,
		},

		hasTabletLayout: Boolean,
	},

	emits: ['submit', 'invalid'],

	setup(props, { slots }) {
		const possibleSlots = ['left', 'right'].flatMap(col => [`${col}-before`, col, `${col}-after`]);
		const appHeader = ref<HTMLElement | null>();

		onMounted(() => {
			appHeader.value = document.querySelector('.app-header') as HTMLElement | null;
		});

		return {
			...useArrayRefs(),

			appHeader,

			isiOS: isiOS(),
			hasSlot: useHasDynamicSlot(possibleSlots, slots),
		};
	},

	computed: {
		stickyOptions(): AnyObject {
			return {
				topSpacing: this.appHeader ? this.appHeader.offsetHeight + PADDING_TOP : PADDING_TOP,
				bottomSpacing: PADDING_BOTTOM,
				resizeSensor: false,
			};
		},

		cssClass() {
			return {
				'page-two-columns--has-tablet-layout': this.hasTabletLayout,
				'page-two-columns--equal': this.division == GridDivision.Equal,
				'page-two-columns--one-third': this.division == GridDivision.OneThird,
				'page-two-columns--one-fourth': this.division == GridDivision.OneFourth,
				'page-two-columns--five-to-seven': this.division == GridDivision.FiveToSeven,
			};
		},

		wrapperTag(): 'form' | 'div' {
			return this.withValidation ? 'form' : 'div';
		},
	},

	methods: {
		isSticky(col: string) {
			return this.sticky === col;
		},

		updateSidebar() {
			const sidebars = this.refsList;

			this.$nextTick(() => {
				sidebars.forEach(sidebar => {
					sidebar.dispatchEvent(new CustomEvent('update.stickySidebar'));
				});
			});
		},

		async onSubmit() {
			this.$emit(this.validate() ? 'submit' : 'invalid');
		},
	},
});
</script>

<style lang="scss">
.page-two-columns {
	$self: &;

	display: grid;
	grid-column-gap: 2.4rem;
	grid-row-gap: 0;
	grid-template-columns: repeat(24, 1fr);
	grid-template-rows: 1fr;
	min-height: 100%;

	@include max-md-and-portrait {
		grid-template-rows: auto 1fr;
	}

	&__column {
		.page-two-columns-column__content {
			padding: 6.5rem 0 12rem;

			@include max-lg-and-portrait {
				padding: 6.2rem 0 4.8rem;
			}
		}

		.page-two-columns-column__sticky-container {
			flex: 1;
		}
	}

	&__column--left {
		grid-column: 1 / span 12;

		@include max-md-and-portrait {
			grid-column: 1 / span 24;
			min-height: 0;
		}
	}

	&__column--right {
		grid-column: 1 / span 12;

		@include max-md-and-portrait {
			grid-column: 1 / span 24;
		}
	}

	&--one-third {
		#{$self}__column--left {
			grid-column: 1 / span 8;

			@include max-lg-and-portrait {
				grid-column: 1 / span 6;
			}

			@include max-md-and-portrait {
				grid-column: 1 / span 24;
			}
		}

		#{$self}__column--right {
			grid-column: 9 / span 16;

			@include max-lg-and-portrait {
				grid-column: 7 / span 18;
			}

			@include max-md-and-portrait {
				grid-column: 1 / span 24;
			}
		}
	}

	&--one-fourth {
		#{$self}__column--left {
			grid-column: 1 / span 6;

			@include max-md-and-portrait {
				grid-column: 1 / span 24;
			}
		}

		#{$self}__column--right {
			grid-column: 7 / span 18;

			@include max-md-and-portrait {
				grid-column: 1 / span 24;
			}
		}
	}

	&--five-to-seven {
		#{$self}__column--left {
			grid-column: 1 / span 10;

			@include max-md-and-portrait {
				grid-column: 1 / span 24;
			}
		}

		#{$self}__column--right {
			grid-column: 11 / span 14;

			@include max-md-and-portrait {
				grid-column: 1 / span 24;
			}
		}
	}

	&--has-tablet-layout {
		&#{$self}--one-third {
			#{$self}__column--left {
				@include max-xl {
					grid-column: 1 / span 24;
				}
			}

			#{$self}__column--right {
				@include max-xl {
					grid-column: 1 / span 24;
				}
			}
		}

		&#{$self}--one-fourth {
			#{$self}__column--left {
				@include max-xl {
					grid-column: 1 / span 24;
				}
			}

			#{$self}__column--right {
				@include max-xl {
					grid-column: 1 / span 24;
				}
			}
		}

		&#{$self}--five-to-seven {
			#{$self}__column--left {
				@include max-xl {
					grid-column: 1 / span 24;
				}
			}

			#{$self}__column--right {
				@include max-xl {
					grid-column: 1 / span 24;
				}
			}
		}
	}
}

.page-two-columns-column {
	&__content {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		height: 100%;
	}

	&.page-two-columns__column--right {
		.page-two-columns-column__content {
			@include max-md-and-portrait {
				padding-top: 3.2rem;
			}
		}
	}

	&__after {
		margin-top: auto;
	}

	&__sticky--native {
		position: sticky;
		top: 14rem;
	}
}
</style>
